import { RefObject, useCallback, useEffect } from 'react';

import {
  Account,
  CashierMessages,
  FieldNames,
  PaymentMethodConfiguration,
  PaymentMethodInput,
} from '@cashier/cashier-model';
import { eventBus, EventFactory, EventTypes } from '@cashier/cashier-shared';

interface UseCashierDataForLeaderIframeParams {
  iframeRef: RefObject<HTMLIFrameElement>;
  schema: Map<FieldNames, PaymentMethodInput>;
  paymentMethod: PaymentMethodConfiguration;
  account: Account;
  iframeOrigin: string;
}

export const useCashierMessages = ({
  iframeRef,
  schema,
  paymentMethod,
  account,
  iframeOrigin,
}: UseCashierDataForLeaderIframeParams): void => {
  const sendPostMessage = useCallback(
    (type: string, payload?: any) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      iframe.contentWindow.postMessage(
        {
          type,
          payload,
        },
        iframeOrigin,
      );
    },
    [iframeOrigin, iframeRef],
  );

  const sendMessage = useCallback(() => {
    sendPostMessage(CashierMessages.CashierData, {
      fields: Array.from(schema.values()),
      paymentMethod,
      account,
    });
  }, [paymentMethod, account, sendPostMessage]);

  useEffect(() => {
    const iframeElement = iframeRef.current;
    const handleLoad = () => {
      eventBus.emit(
        EventFactory.cashierMessage({
          task: CashierMessages.CashierData,
        }),
      );
    };

    iframeElement.addEventListener('load', handleLoad);
    return () => {
      iframeElement.removeEventListener('load', handleLoad);
    };
  }, [iframeRef]);

  useEffect(() => {
    return eventBus.on<{
      task?: string;
      processingResult?: unknown;
    }>(EventTypes.MESSAGE_TO_LEADER_IFRAME, (event) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;

      const { task, processingResult } = event.payload || {};

      if (task === CashierMessages.SendRequestForFinteqHubMethods) {
        sendPostMessage(CashierMessages.SendRequestForFinteqHubMethods, {
          ...(processingResult as Record<string, unknown>),
        });
        return;
      }

      if (
        task === CashierMessages.GetEncryptedFieldsData ||
        task === CashierMessages.LoadPliromyTokenizer
      ) {
        sendPostMessage(task);
        return;
      }

      if (task === CashierMessages.CashierData) {
        sendMessage();
      }
    });
  }, [iframeRef, sendMessage, sendPostMessage]);

  useEffect(() => {
    eventBus.emit(
      EventFactory.cashierMessage({
        task: CashierMessages.CashierData,
      }),
    );
  }, [paymentMethod, account]);
};
